import type {
  CreateCustomerNoteResponse,
  CustomerNoteRecord,
  GetCustomerContactResponse,
  ListCustomerNotesResponse,
  UpsertCustomerContactRequest,
  UpsertCustomerContactResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * CRM contact card + append-only follow-up notes (epic #1035 E6-D5,
 * #1069). Self-contained API surface for `CustomerCrmPanel` — all four
 * calls ride the customer-manager v2 prefix, so ops-api enforces the
 * owner / same-brand-lead scope server-side. There is deliberately no
 * update or delete call for notes anywhere in this module.
 */

const BASE = '/v1/ops/v2/customer-manager';

export const CUSTOMER_CRM_ROOT_KEY = ['customer-crm'] as const;

export function customerContactQueryKey(
  orgId: string,
): readonly ['customer-crm', 'contact', string] {
  return ['customer-crm', 'contact', orgId] as const;
}

export function customerNotesQueryKey(
  orgId: string,
  page: { readonly limit: number; readonly offset: number },
): readonly ['customer-crm', 'notes', string, { readonly limit: number; readonly offset: number }] {
  return ['customer-crm', 'notes', orgId, page] as const;
}

export async function getCustomerContact(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetCustomerContactResponse> {
  return opsApiRequest<GetCustomerContactResponse>(
    `${BASE}/customers/${encodeURIComponent(orgId)}/contact`,
    { signal },
    fetchImpl,
  );
}

export async function upsertCustomerContact(
  orgId: string,
  body: UpsertCustomerContactRequest,
  fetchImpl: typeof fetch,
): Promise<UpsertCustomerContactResponse> {
  return opsApiRequest<UpsertCustomerContactResponse>(
    `${BASE}/customers/${encodeURIComponent(orgId)}/contact`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function listCustomerNotes(
  orgId: string,
  page: { readonly limit: number; readonly offset: number },
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListCustomerNotesResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(page.limit));
  params.set('offset', String(page.offset));
  return opsApiRequest<ListCustomerNotesResponse>(
    `${BASE}/customers/${encodeURIComponent(orgId)}/notes?${params.toString()}`,
    { signal },
    fetchImpl,
  );
}

export async function createCustomerNote(
  orgId: string,
  body: { readonly body: string },
  fetchImpl: typeof fetch,
): Promise<CreateCustomerNoteResponse> {
  return opsApiRequest<CreateCustomerNoteResponse>(
    `${BASE}/customers/${encodeURIComponent(orgId)}/notes`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export function useCustomerContact(orgId: string): UseQueryResult<GetCustomerContactResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: customerContactQueryKey(orgId),
    queryFn: ({ signal }) => getCustomerContact(orgId, fetchImpl, signal),
    enabled: orgId.length > 0,
  });
}

export function useUpsertCustomerContact(): UseMutationResult<
  UpsertCustomerContactResponse,
  Error,
  { orgId: string; body: UpsertCustomerContactRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }) => upsertCustomerContact(orgId, body, fetchImpl),
    onSuccess: (data, vars) => {
      qc.setQueryData(customerContactQueryKey(vars.orgId), {
        contact: data.contact,
      } satisfies GetCustomerContactResponse);
    },
  });
}

export function useCustomerNotes(
  orgId: string,
  page: { readonly limit: number; readonly offset: number },
): UseQueryResult<ListCustomerNotesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: customerNotesQueryKey(orgId, page),
    queryFn: ({ signal }) => listCustomerNotes(orgId, page, fetchImpl, signal),
    enabled: orgId.length > 0,
  });
}

export function useCreateCustomerNote(): UseMutationResult<
  CreateCustomerNoteResponse,
  Error,
  { orgId: string; body: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }) => createCustomerNote(orgId, { body }, fetchImpl),
    onSuccess: (data, vars) => {
      // Prepend to the cached first page so the fresh note appears
      // without a reload; the invalidate below reconciles totals and
      // any later pages with the server.
      qc.setQueriesData<ListCustomerNotesResponse>(
        { queryKey: [...CUSTOMER_CRM_ROOT_KEY, 'notes', vars.orgId] },
        (existing) => {
          if (existing === undefined || existing.offset !== 0) {
            return existing;
          }
          const notes: CustomerNoteRecord[] = [data.note, ...existing.notes];
          return { ...existing, notes, total: existing.total + 1 };
        },
      );
      void qc.invalidateQueries({ queryKey: [...CUSTOMER_CRM_ROOT_KEY, 'notes', vars.orgId] });
    },
  });
}
